'use client';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ChevronLeft,
  Send,
  Calendar,
  MapPin,
  Clock,
  Users,
  Heart,
  MessageSquare,
  ChevronRight,
} from 'lucide-react';
import { Avatar, AvatarImage } from '../ui/avatar.tsx';
import { Button } from '../ui/button.tsx';
import { Card, CardContent } from '../ui/card.tsx';
import { Input } from '../ui/input.tsx';
import { cn } from '../../lib/utils.ts';

export function EventDetails({ event }) {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);

  if (!event) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900">Event not found</h2>
        <p className="mt-2 text-gray-600">
          The event you're looking for doesn't exist or has been removed.
        </p>
        <Button className="mt-4" onClick={() => navigate('/events')}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
      </div>
    );
  }

  const eventDate = new Date(event.eventDate);
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);

  const MainContent = () => (
    <Card className="overflow-hidden">
      <div className="relative h-64 bg-gradient-to-br from-purple-600 via-purple-500 to-purple-700">
        {/* Decorative background pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px',
          }}
        />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-8">
          {/* Top section with type and date */}
          <div className="flex items-center justify-between">
            <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
              {event.type}
            </span>
            <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm">
              {format(eventDate, 'MMMM d, yyyy')}
            </span>
          </div>

          {/* Bottom section with title and organizer */}
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">
              {event.eventTitle}
            </h2>

            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 ring-2 ring-white/30 ring-offset-2 ring-offset-purple-600">
                <AvatarImage
                  src={event.participants?.[0]?.image || '/avatars/default.png'}
                  alt={event.participants?.[0]?.name}
                  className="object-cover"
                />
              </Avatar>
              <div>
                <div className="font-medium text-white">
                  {event.participants?.[0]?.name || 'Event Organizer'}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <span>Event Organizer</span>
                  <span className="h-1 w-1 rounded-full bg-white/50" />
                  <span>{event.location}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 p-4 bg-purple-50 rounded-xl">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
              <Calendar className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xs text-gray-500 font-medium">Date</div>
                <div className="text-sm font-medium">
                  {format(eventDate, 'MMMM d, yyyy')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
              <Clock className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xs text-gray-500 font-medium">Time</div>
                <div className="text-sm font-medium">
                  {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
              <MapPin className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xs text-gray-500 font-medium">
                  Location
                </div>
                <div className="text-sm font-medium">{event.location}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
              <Users className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xs text-gray-500 font-medium">
                  Participants
                </div>
                <div className="text-sm font-medium">
                  {event.participants?.length || 0} people
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
            <h3 className="font-medium text-gray-900 mb-3">About this event</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {event.description}
            </p>
          </div>

          {event.recurring && (
            <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
              <h3 className="font-medium text-gray-900 mb-3">
                Recurring Event
              </h3>
              <p className="text-sm text-purple-600 leading-relaxed">
                This is a recurring event -{' '}
                {event.recurrenceRule.replace('FREQ=', '').toLowerCase()}{' '}
                occurrence
              </p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-700 hover:text-red-600 font-medium"
              >
                <Heart className="h-4 w-4 mr-2" />
                {event.likes || 0}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-700 font-medium"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {event.comments || 0}
              </Button>
            </div>
            <Button
              onClick={() => setShowComments(true)}
              className="text-purple-600 hover:text-purple-700 font-medium"
              variant="ghost"
            >
              More Details
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CommentsSection = () => (
    <Card className="h-full">
      <CardContent className="p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-medium text-gray-900">Comments & Reactions</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowComments(false)}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto">
          {event.comments > 0 &&
            Array.from({ length: Math.min(5, event.comments) }).map((_, i) => (
              <div
                key={i}
                className="flex gap-3 pb-6 border-b last:border-0 last:pb-0"
              >
                <Avatar className="h-8 w-8 ring-2 ring-purple-100 bg-purple-50">
                  <AvatarImage
                    src={
                      event.participants?.[i + 1]?.image ||
                      '/avatars/default.png'
                    }
                    alt={event.participants?.[i + 1]?.name}
                    className="object-cover"
                  />
                </Avatar>
                <div className="space-y-2 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-gray-900">
                      {event.participants?.[i + 1]?.name || 'Anonymous'}
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(new Date(), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Looking forward to this event! The agenda looks great and
                    I'm sure it will be very informative.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-gray-600 hover:text-red-600 font-medium"
                    >
                      <Heart className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs">3</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-gray-600 font-medium"
                    >
                      <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs">Reply</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="flex gap-2 items-center sticky bottom-0 bg-white pt-4 border-t mt-6">
          <Input
            className="flex-1 bg-gray-50 border-gray-200 focus-visible:ring-purple-500 text-sm"
            placeholder="Write a comment..."
          />
          <Button
            size="icon"
            className="bg-purple-600 hover:bg-purple-700 rounded-full h-9 w-9 shadow-lg shadow-purple-200"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-gray-900"
          onClick={() => navigate('/events')}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Button>
      </div>

      <div
        className={cn(
          'grid gap-6 transition-all duration-300 ease-in-out',
          showComments ? 'grid-cols-2' : 'grid-cols-1 max-w-3xl mx-auto'
        )}
      >
        <div
          className={cn(
            'transition-all duration-300',
            showComments ? 'w-full' : 'w-full'
          )}
        >
          <MainContent />
        </div>

        {showComments && (
          <div className="w-full">
            <CommentsSection />
          </div>
        )}
      </div>
    </div>
  );
}
